// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { setSessionStorage } from '@tcp/core/src/utils';
import Icon from './Icon.view';
import {
  StyledButtonWrapper,
  StyledButton,
  StyledButtonLabelWrapper,
  StyleButtonLabel,
} from '../styles/Switch.style';

const getFillColor = (selectedOption, label) => {
  return selectedOption === label ? '#595959' : '#9B9B9B';
};

const getCurrentAccessibleLabel = (
  selectedOption,
  firstLabel,
  firstAccessibleLabel,
  secondAccessibleLabel
) => {
  return selectedOption === firstLabel ? firstAccessibleLabel : secondAccessibleLabel;
};

const getOtherAccessibleLabel = (
  selectedOption,
  firstLabel,
  firstAccessibleLabel,
  secondAccessibleLabel
) => {
  return selectedOption === firstLabel ? secondAccessibleLabel : firstAccessibleLabel;
};

const SwitchToggle = ({ className, selectedOption, setSelectedOption, options, storageKey }) => {
  const {
    iconPath: firstOptionIconPath,
    label: firstOptionLabel,
    accessibleLabel: firstOptionAccessibleLabel,
  } = options[0];
  const {
    iconPath: secondOptionIconPath,
    label: secondOptionLabel,
    accessibleLabel: secondOptionAccessibleLabel,
  } = options[1];

  const handleToggle = (clickedOption) => {
    if (clickedOption && clickedOption === selectedOption) return;
    const updatedOption =
      selectedOption === firstOptionLabel ? secondOptionLabel : firstOptionLabel;
    setSelectedOption(updatedOption);
    if (storageKey) {
      setSessionStorage({ key: storageKey, value: updatedOption });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <StyledButtonWrapper
      role="switch"
      className={className}
      aria-checked={selectedOption === secondOptionLabel}
      tabIndex={0}
      aria-label={`Selected option is ${getCurrentAccessibleLabel(
        selectedOption,
        firstOptionLabel,
        firstOptionAccessibleLabel,
        secondOptionAccessibleLabel
      )}. Press space bar or enter to toggle the selection to ${getOtherAccessibleLabel(
        selectedOption,
        firstOptionLabel,
        firstOptionAccessibleLabel,
        secondOptionAccessibleLabel
      )}.`}
      onKeyDown={handleKeyDown}
    >
      <StyledButton
        type="button"
        className="first-option-button"
        onClick={() => handleToggle(firstOptionLabel)}
        disabled={selectedOption === firstOptionLabel}
        tabIndex={-1}
      >
        <StyledButtonLabelWrapper>
          {firstOptionIconPath && (
            <Icon
              icon={firstOptionIconPath}
              fill={getFillColor(selectedOption, firstOptionLabel)}
            />
          )}
          <StyleButtonLabel
            className="toggle-button-label first-label"
            fontFamily="secondary"
            fontSize="fs12"
            fontWeight="bold"
          >
            {firstOptionLabel}
          </StyleButtonLabel>
        </StyledButtonLabelWrapper>
      </StyledButton>
      <StyledButton
        type="button"
        className="second-option-button"
        onClick={() => handleToggle(secondOptionLabel)}
        disabled={selectedOption === secondOptionLabel}
        tabIndex={-1}
      >
        <StyledButtonLabelWrapper>
          {secondOptionIconPath && (
            <Icon
              icon={secondOptionIconPath}
              fill={getFillColor(selectedOption, secondOptionLabel)}
            />
          )}
          <StyleButtonLabel
            className="toggle-button-label second-label"
            fontFamily="secondary"
            fontSize="fs12"
            fontWeight="bold"
          >
            {secondOptionLabel}
          </StyleButtonLabel>
        </StyledButtonLabelWrapper>
      </StyledButton>
    </StyledButtonWrapper>
  );
};

SwitchToggle.propTypes = {
  className: PropTypes.string,
  selectedOption: PropTypes.string.isRequired,
  setSelectedOption: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      iconPath: PropTypes.string,
      label: PropTypes.string,
      accessibleLabel: PropTypes.string,
    })
  ).isRequired,
  storageKey: PropTypes.string,
};

SwitchToggle.defaultProps = {
  className: '',
  storageKey: '',
};

export default SwitchToggle;
